import React from "react";
import { ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AssetIcon from "@/components/AssetIcon";

const catLabels = { crypto: "Kripto", forex: "Forex", commodities: "Emtia", stocks: "Hisse" };
const catColors  = { crypto: "var(--iq-green)", forex: "#64b5f6", commodities: "#ffb74d", stocks: "#9c27b0" };

export default function AssetSelector({ assets: assetsProp, current, tickers, onSelect }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [cat, setCat] = React.useState("all");
  const assets = Array.isArray(assetsProp) ? assetsProp : [];
  const cats = ["all", ...Array.from(new Set(assets.map((a) => a.category)))];
  const filtered = assets.filter((a) =>
    (cat === "all" || a.category === cat) &&
    (a.name.toLowerCase().includes(query.toLowerCase()) || a.symbol.toLowerCase().includes(query.toLowerCase()))
  );
  const curPrice = tickers[current?.symbol]?.price;

  const handleSelect = (a) => {
    onSelect(a);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="asset-selector"
          className="flex items-center gap-3 bg-panel border border-line px-3 py-2 hover:bg-panel-hover rounded-sm"
        >
          {/* Current asset icon */}
          <AssetIcon symbol={current?.symbol} category={current?.category} size={32} />

          <div className="text-left leading-tight">
            <div className="text-sm font-medium" data-testid="current-asset-name">
              {current?.name}
            </div>
            <div className="text-[10px] text-dim tracking-widest uppercase flex items-center gap-1">
              <span style={{ color: catColors[current?.category] }}>
                {catLabels[current?.category] || current?.category}
              </span>
              <span>·</span>
              <span>{current?.payout}% payout</span>
            </div>
          </div>

          {curPrice != null && (
            <div className="ml-3 font-mono text-buy text-base" data-testid="current-asset-price">
              {curPrice.toLocaleString("en-US", {
                minimumFractionDigits: current?.digits ?? 2,
                maximumFractionDigits: current?.digits ?? 2,
              })}
            </div>
          )}
          <ChevronDown className="w-4 h-4 text-muted" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="bg-panel border-line text-white w-[400px] p-0" align="start">
        {/* Search bar */}
        <div className="p-3 border-b border-line flex items-center gap-2">
          <Search className="w-4 h-4 text-dim" />
          <input
            data-testid="asset-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ara..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-dim"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              style={{ color: "var(--iq-text-dim)", fontSize: 11, background: "none", border: "none", cursor: "pointer" }}
            >✕</button>
          )}
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 p-2 border-b border-line">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c}`}
              onClick={() => setCat(c)}
              style={{
                padding: "5px 10px",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                borderRadius: 4,
                border: "none",
                cursor: "pointer",
                fontWeight: cat === c ? 700 : 500,
                background: cat === c
                  ? (c === "all" ? "var(--iq-green)" : catColors[c] ?? "var(--iq-green)")
                  : "transparent",
                color: cat === c ? "#000" : "var(--iq-text-dim)",
                transition: "all 0.15s",
              }}
            >
              {c === "all" ? "Tümü" : catLabels[c] || c}
            </button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--iq-text-dim)", display: "flex", alignItems: "center", paddingRight: 4 }}>
            {filtered.length} enstrüman
          </span>
        </div>

        {/* Asset list */}
        <div className="overflow-y-auto" style={{ maxHeight: 360 }}>
          {filtered.length === 0 && (
            <div style={{ padding: "24px 0", textAlign: "center", color: "var(--iq-text-dim)", fontSize: 13 }}>
              Sonuç bulunamadı
            </div>
          )}
          {filtered.map((a) => {
            const t = tickers[a.symbol];
            const chg = t?.change ?? 0;
            const isActive = current?.symbol === a.symbol;
            return (
              <button
                key={a.symbol}
                data-testid={`asset-item-${a.symbol}`}
                onClick={() => handleSelect(a)}
                className="w-full flex items-center justify-between px-3 py-2 transition-colors border-b border-subtle hover:bg-panel-hover"
                style={{
                  background: isActive ? "rgba(0,230,118,0.06)" : undefined,
                  borderLeft: isActive ? "2px solid var(--iq-green)" : "2px solid transparent",
                }}
              >
                <div className="flex items-center gap-3">
                  <AssetIcon symbol={a.symbol} category={a.category} size={28} />
                  <div className="text-left">
                    <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? "#fff" : "var(--iq-text-bright)" }}>
                      {a.name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        {a.symbol}
                      </span>
                      <span style={{
                        fontSize: 9, fontWeight: 700,
                        color: catColors[a.category] ?? "var(--iq-text-dim)",
                        background: (catColors[a.category] ?? "#888") + "22",
                        padding: "1px 5px", borderRadius: 3,
                        textTransform: "uppercase", letterSpacing: "0.06em",
                      }}>
                        {catLabels[a.category] || a.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#fff" }}>
                    {t?.price
                      ? t.price.toLocaleString("en-US", { minimumFractionDigits: a.digits ?? 2, maximumFractionDigits: a.digits ?? 2 })
                      : "—"}
                  </div>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    color: chg >= 0 ? "var(--iq-green)" : "var(--iq-red)",
                    fontWeight: 600,
                  }}>
                    {chg >= 0 ? "+" : ""}{chg.toFixed(2)}%
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
